import { FilterBar, LoadMore, ListEmpty, MoreInOtherMonths, useListUrlState, applySearch, sortBy, takeLoaded } from '@/components/lists';
import { CHECK_HISTORY, last7Cutoff, type AllCheckRow } from './historyMock';

const STEP = 50;

function textOf(c: AllCheckRow): string {
  return `${c.name} ${c.payer} ${c.memberId} ${c.outcome} ${c.by} ${c.dateLabel}`;
}

const OUTCOME_CLS: Record<string, string> = { Active: 'ok', Partial: 'warn', 'Not found': 'warn', Terminated: 'warn' };

/**
 * Live Check — all live checks, at scale (00-ds-09 §9, D148). Last-7-days
 * window by default, a search, and "Show 50 more" (a load-more queue, not
 * numbered pages). Client-side over the mock; the server owns this (§8).
 */
export function AllChecks({ onOpen, onBack }: { onOpen: (id: string) => void; onBack: () => void }) {
  const [ls, patch] = useListUrlState({ q: '', win: '7', n: String(STEP) });
  const win = ls.win === 'all' ? 'all' : '7';
  const loaded = Math.max(STEP, Number(ls.n) || STEP);
  const cutoff = last7Cutoff();

  const windowed = win === 'all' ? CHECK_HISTORY : CHECK_HISTORY.filter((c) => c.dateIso >= cutoff);
  const searched = sortBy(applySearch(windowed, ls.q, textOf), (c) => c.dateIso, -1);
  const visible = takeLoaded(searched, loaded);

  const allMatches = ls.q.trim() !== '' ? applySearch(CHECK_HISTORY, ls.q, textOf).length : 0;
  const outsideWindow = win === '7' ? Math.max(0, allMatches - searched.length) : 0;

  return (
    <div className="lc">
      <div className="lc-allhead">
        <button type="button" className="lc-src" onClick={onBack}>← Back to Live Check</button>
        <h1 className="lc-h1" style={{ margin: '6px 0 0' }}>
          All live checks {win === 'all' ? '· all time' : '· last 7 days'} · {searched.length.toLocaleString()}
        </h1>
      </div>

      <FilterBar
        search={ls.q}
        onSearch={(v) => { patch({ q: v, n: String(STEP) }); }}
        searchPlaceholder="Search name, payer, member ID or who ran it"
        showDateRange={false}
        right={
          <button type="button" className="lc-src" onClick={() => { patch({ win: win === '7' ? 'all' : '7', n: String(STEP) }); }}>
            {win === '7' ? 'Show all time' : 'Show last 7 days'}
          </button>
        }
      />

      {searched.length === 0 ? (
        <ListEmpty
          {...(ls.q.trim() !== '' ? { query: ls.q } : {})}
          {...(win === '7' ? { windowLabel: 'the last 7 days' } : {})}
          {...(win === '7' && outsideWindow > 0 ? { onSearchAllTime: () => { patch({ win: 'all', n: String(STEP) }); } } : {})}
        />
      ) : (
        <>
          <div className="lc-allhead-row">
            <span>Patient</span><span>Payer</span><span>Member ID</span><span>When</span><span>Outcome</span>
          </div>
          <div className="lc-alllist">
            {visible.map((c) => (
              <button key={c.id} type="button" className="lc-allrow" onClick={() => { onOpen(c.id); }}>
                <span className="lc-allname">{c.name}<span className="lc-allsub">ran by {c.by}</span></span>
                <span className="lc-allmono">{c.payer}</span>
                <span className="lc-allmono">{c.memberId}</span>
                <span className="lc-allmono">{c.dateLabel} · {c.at}</span>
                <span><span className={`lc-tag lc-tag-${OUTCOME_CLS[c.outcome] ?? 'neutral'}`}>{c.outcome}</span></span>
              </button>
            ))}
          </div>
          <LoadMore loaded={visible.length} total={searched.length} step={STEP} onMore={() => { patch({ n: String(loaded + STEP) }); }} />
          {win === '7' ? <MoreInOtherMonths count={outsideWindow} onShowAllTime={() => { patch({ win: 'all', n: String(STEP) }); }} /> : null}
        </>
      )}
    </div>
  );
}
